import { unwrapEnvelopeOr } from '@/model/client';
import { useGetApiWorkspacesByIdQuotaQuery } from '@/model/quota/api.generated';
import type { Blocking, LineUsage, OrgUsageResponse } from '@/model/quota/dto.generated';
import { useAppSelector } from '@/shared/hooks/useStore';
import { selectCurrentWorkspace } from '@/viewmodel/features/workspace';

const QUOTA_POLL_MS = 5 * 60 * 1000;
const BYTES_PER_GB = 1e9;

export type QuotaBlockedAction = keyof Blocking;

export type QuotaBlock = {
  line: 'storage' | 'processing';
  usedGb: number;
  limitGb: number;
  blocked: QuotaBlockedAction[];
  resetsAt: string | null;
};

const ACTIONS: readonly QuotaBlockedAction[] = ['create', 'refresh', 'query'];

function toGb(bytes: number): number {
  return Math.round((bytes / BYTES_PER_GB) * 10) / 10;
}

function blockOf(line: LineUsage): QuotaBlock | null {
  const blocked = ACTIONS.filter((action) => line.blocking?.[action] === true);
  if (blocked.length === 0 || !line.line || typeof line.quota_bytes !== 'number') return null;

  return {
    line: line.line,
    usedGb: toGb(line.usage_bytes ?? 0),
    limitGb: toGb(line.quota_bytes),
    blocked,
    resetsAt: line.resets_at ?? null,
  };
}

export function quotaBlocksOf(lines: readonly LineUsage[]): QuotaBlock[] {
  return lines.map(blockOf).filter((block): block is QuotaBlock => block !== null);
}

export function useQuotaBlocks(): { blocks: QuotaBlock[] } {
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = workspace?.id ?? '';
  const { data } = useGetApiWorkspacesByIdQuotaQuery(
    { id: workspaceId },
    { skip: !workspaceId, pollingInterval: QUOTA_POLL_MS },
  );
  const quota = unwrapEnvelopeOr<OrgUsageResponse | null>(data, null);

  return { blocks: quotaBlocksOf(quota?.lines ?? []) };
}
